// ═══════════════════════════════════════════════════════════════════════════
// FARM-BOOKING IoT - GESTION MODULES ESP32
// ═══════════════════════════════════════════════════════════════════════════
// frontend/src/modules/admin/components/GestionIoTModules.jsx
// Interface CRUD complète pour les modules ESP32
// Papy 73+ - Joshua approved! 🚀
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════
export default function GestionIoTModules() {
  // États
  const [modules, setModules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit' | 'view'
  const [formData, setFormData] = useState(getEmptyFormData());
  const [filter, setFilter] = useState({ statut: '', protocole: '' });

  // ═══════════════════════════════════════════════════════════════════════════
  // CHARGEMENT DES DONNÉES
  // ═══════════════════════════════════════════════════════════════════════════
  const loadModules = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter.statut) params.append('statut', filter.statut);
      if (filter.protocole) params.append('protocole', filter.protocole);
      
      const response = await fetch(`/api/iot/modules?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setModules(data.data);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Erreur de chargement des modules');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const loadTemplates = async () => {
    try {
      const response = await fetch('/api/iot/templates');
      const data = await response.json();
      if (data.success) {
        setTemplates(data.data);
      }
    } catch (err) {
      console.error('Erreur chargement templates:', err);
    }
  };

  useEffect(() => {
    loadModules();
    loadTemplates();
  }, [loadModules]);

  // Refresh automatique toutes les 30s
  useEffect(() => {
    const interval = setInterval(loadModules, 30000);
    return () => clearInterval(interval);
  }, [loadModules]);

  // ═══════════════════════════════════════════════════════════════════════════
  // CRUD OPERATIONS
  // ═══════════════════════════════════════════════════════════════════════════
  const handleCreate = () => {
    setFormData(getEmptyFormData());
    setModalMode('create');
    setShowModal(true);
  };

  const handleEdit = (module) => {
    setFormData({
      ...module,
      config_seuils: module.config_seuils || {}
    });
    setModalMode('edit');
    setShowModal(true);
  };

  const handleView = async (module) => {
    try {
      const response = await fetch(`/api/iot/modules/${module.id}`);
      const data = await response.json();
      if (data.success) {
        setSelectedModule(data.data);
        setModalMode('view');
        setShowModal(true);
      }
    } catch (err) {
      console.error('Erreur chargement détail:', err);
    }
  };

  const handleSave = async () => {
    try {
      const url = modalMode === 'create' 
        ? '/api/iot/modules' 
        : `/api/iot/modules/${formData.id}`;
      
      const method = modalMode === 'create' ? 'POST' : 'PUT';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setShowModal(false);
        loadModules();
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Erreur de sauvegarde');
      console.error(err);
    }
  };

  const handleDelete = async (module) => {
    if (!window.confirm(`Supprimer le module "${module.nom}" ?`)) return;
    
    try {
      const response = await fetch(`/api/iot/modules/${module.id}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        loadModules();
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Erreur de suppression');
      console.error(err);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // CONFIGURATION & SYNC
  // ═══════════════════════════════════════════════════════════════════════════
  const handleOpenConfig = (module) => {
    setSelectedModule(module);
    setFormData({
      ...module,
      config_seuils: module.config_seuils || getDefaultSeuils()
    });
    setShowConfigModal(true);
  };

  const handleSyncConfig = async () => {
    try {
      const response = await fetch(`/api/iot/modules/${selectedModule.id}/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          config_seuils: formData.config_seuils,
          interval_mesure: formData.interval_mesure
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setShowConfigModal(false);
        loadModules();
        alert('✅ Configuration synchronisée !');
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Erreur de synchronisation');
      console.error(err);
    }
  };

  const handleApplyTemplate = async (templateId) => {
    try {
      const response = await fetch(`/api/iot/modules/${selectedModule.id}/apply-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ template_id: templateId })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setFormData(prev => ({
          ...prev,
          config_seuils: data.config_seuils
        }));
        alert(`✅ Template appliqué !`);
      }
    } catch (err) {
      console.error('Erreur application template:', err);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER - TABLEAU DES MODULES
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            📡 Modules IoT ESP32
          </h1>
          <p className="text-gray-600">Gestion des {modules.length} module(s) de la ferme</p>
        </div>
        
        <button
          onClick={handleCreate}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <span>➕</span> Nouveau Module
        </button>
      </div>

      {/* Erreur */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Filtres */}
      <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-wrap gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
          <select
            value={filter.statut}
            onChange={(e) => setFilter(prev => ({ ...prev, statut: e.target.value }))}
            className="border rounded px-3 py-2 text-sm"
          >
            <option value="">Tous</option>
            <option value="online">🟢 En ligne</option>
            <option value="offline">🔴 Hors ligne</option>
            <option value="erreur">⚠️ Erreur</option>
            <option value="maintenance">🔧 Maintenance</option>
            <option value="nouveau">🆕 Nouveau</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Protocole</label>
          <select
            value={filter.protocole}
            onChange={(e) => setFilter(prev => ({ ...prev, protocole: e.target.value }))}
            className="border rounded px-3 py-2 text-sm"
          >
            <option value="">Tous</option>
            <option value="wifi">📶 WiFi</option>
            <option value="lora">📻 LoRa</option>
            <option value="ethernet">🔌 Ethernet</option>
          </select>
        </div>
        
        <div className="flex items-end">
          <button
            onClick={loadModules}
            className="bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded text-sm flex items-center gap-1"
          >
            🔄 Rafraîchir
          </button>
        </div>
      </div>

      {/* Tableau des modules */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Chargement...</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Module</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Localisation</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Protocole</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dernière connexion</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Capteurs</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {modules.map((module) => (
                  <tr key={module.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className="font-medium text-gray-900">{module.nom}</div>
                      <div className="text-sm text-gray-500 font-mono">{module.code_module}</div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">
                      {module.localisation || '-'}
                    </td>
                    <td className="px-4 py-4">
                      <StatusBadge status={module.statut} etat={module.etat_connexion} />
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <ProtocoleBadge protocole={module.protocole} />
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">
                      {module.derniere_connexion 
                        ? formatDate(module.derniere_connexion)
                        : <span className="text-gray-400">Jamais</span>
                      }
                      {module.minutes_depuis_connexion && (
                        <div className="text-xs text-gray-400">
                          il y a {formatDuration(module.minutes_depuis_connexion)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-center">
                      <span className="bg-gray-100 px-2 py-1 rounded-full text-gray-700">
                        {module.nb_capteurs || 0}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => handleView(module)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          title="Voir détails"
                        >
                          👁️
                        </button>
                        <button
                          onClick={() => handleOpenConfig(module)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded"
                          title="Configurer seuils"
                        >
                          ⚙️
                        </button>
                        <button
                          onClick={() => handleEdit(module)}
                          className="p-2 text-yellow-600 hover:bg-yellow-50 rounded"
                          title="Modifier"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(module)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="Supprimer"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                
                {modules.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                      Aucun module trouvé
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal Création/Edition */}
      {showModal && modalMode !== 'view' && (
        <Modal
          title={modalMode === 'create' ? '➕ Nouveau Module' : '✏️ Modifier Module'}
          onClose={() => setShowModal(false)}
        >
          <ModuleForm 
            formData={formData} 
            setFormData={setFormData}
            onSave={handleSave}
            onCancel={() => setShowModal(false)}
          />
        </Modal>
      )}

      {/* Modal Vue Détaillée */}
      {showModal && modalMode === 'view' && selectedModule && (
        <Modal
          title={`📟 ${selectedModule.nom}`}
          onClose={() => setShowModal(false)}
          large
        >
          <ModuleDetail module={selectedModule} />
        </Modal>
      )}

      {/* Modal Configuration Seuils */}
      {showConfigModal && selectedModule && (
        <Modal
          title={`⚙️ Configuration - ${selectedModule.nom}`}
          onClose={() => setShowConfigModal(false)}
          large
        >
          <ConfigPanel
            module={selectedModule}
            formData={formData}
            setFormData={setFormData}
            templates={templates}
            onSync={handleSyncConfig}
            onApplyTemplate={handleApplyTemplate}
            onCancel={() => setShowConfigModal(false)}
          />
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANTS SECONDAIRES
// ═══════════════════════════════════════════════════════════════════════════

function StatusBadge({ status, etat }) {
  const styles = {
    online: 'bg-green-100 text-green-800',
    offline: 'bg-red-100 text-red-800',
    erreur: 'bg-orange-100 text-orange-800',
    maintenance: 'bg-yellow-100 text-yellow-800',
    nouveau: 'bg-blue-100 text-blue-800'
  };
  
  const icons = {
    online: '🟢',
    offline: '🔴',
    erreur: '⚠️',
    maintenance: '🔧',
    nouveau: '🆕'
  };
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
      {icons[status]} {status}
      {etat === 'retard' && <span className="ml-1 text-yellow-600">⏱️</span>}
    </span>
  );
}

function ProtocoleBadge({ protocole }) {
  const icons = {
    wifi: '📶',
    lora: '📻',
    ethernet: '🔌',
    'wifi+lora': '📶📻'
  };
  
  return (
    <span className="text-gray-700">
      {icons[protocole] || '❓'} {protocole}
    </span>
  );
}

function Modal({ title, children, onClose, large = false }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-lg shadow-xl w-full ${large ? 'max-w-4xl' : 'max-w-2xl'} max-h-[90vh] overflow-hidden flex flex-col`}>
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <h2 className="text-xl font-bold text-gray-800">{title}</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
          >
            ×
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {children}
        </div>
      </div>
    </div>
  );
}

function ModuleForm({ formData, setFormData, onSave, onCancel }) {
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Code Module *
          </label>
          <input
            type="text"
            value={formData.code_module || ''}
            onChange={(e) => handleChange('code_module', e.target.value)}
            placeholder="ESP-SERRE-01"
            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nom *
          </label>
          <input
            type="text"
            value={formData.nom || ''}
            onChange={(e) => handleChange('nom', e.target.value)}
            placeholder="Module Serre Tomates"
            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          value={formData.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          rows={2}
          className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Localisation
          </label>
          <input
            type="text"
            value={formData.localisation || ''}
            onChange={(e) => handleChange('localisation', e.target.value)}
            placeholder="Serre Nord - Parcelle A"
            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Protocole
          </label>
          <select
            value={formData.protocole || 'wifi'}
            onChange={(e) => handleChange('protocole', e.target.value)}
            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option value="wifi">📶 WiFi</option>
            <option value="lora">📻 LoRa</option>
            <option value="ethernet">🔌 Ethernet</option>
            <option value="wifi+lora">📶📻 WiFi + LoRa</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Adresse MAC
          </label>
          <input
            type="text"
            value={formData.mac_address || ''}
            onChange={(e) => handleChange('mac_address', e.target.value)}
            placeholder="AA:BB:CC:DD:EE:FF"
            className="w-full border rounded-lg px-3 py-2 font-mono focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Intervalle mesures (min)
          </label>
          <input
            type="number"
            value={formData.interval_mesure || 15}
            onChange={(e) => handleChange('interval_mesure', parseInt(e.target.value))}
            min={1}
            max={60}
            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          onClick={onCancel}
          className="px-4 py-2 border rounded-lg hover:bg-gray-50"
        >
          Annuler
        </button>
        <button
          onClick={onSave}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          💾 Enregistrer
        </button>
      </div>
    </div>
  );
}

function ModuleDetail({ module }) {
  return (
    <div className="space-y-6">
      {/* Info générales */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <InfoCard label="Code" value={module.code_module} mono />
        <InfoCard label="Statut" value={<StatusBadge status={module.statut} />} />
        <InfoCard label="Protocole" value={<ProtocoleBadge protocole={module.protocole} />} />
        <InfoCard label="Firmware" value={module.firmware_version} mono />
      </div>

      {/* Status hardware */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="font-medium text-gray-700 mb-3">📊 État Hardware</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Batterie:</span>
            <span className="ml-2 font-medium">
              {module.batterie_niveau ? `${module.batterie_niveau}%` : '-'}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Tension:</span>
            <span className="ml-2 font-medium">
              {module.batterie_tension ? `${module.batterie_tension}V` : '-'}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Signal:</span>
            <span className="ml-2 font-medium">
              {module.signal_rssi ? `${module.signal_rssi} dBm` : '-'}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Uptime:</span>
            <span className="ml-2 font-medium">
              {module.uptime_heures ? `${module.uptime_heures}h` : '-'}
            </span>
          </div>
        </div>
      </div>

      {/* Capteurs associés */}
      {module.capteurs && module.capteurs.length > 0 && (
        <div>
          <h3 className="font-medium text-gray-700 mb-3">🔌 Capteurs ({module.capteurs.length})</h3>
          <div className="space-y-2">
            {module.capteurs.map((c) => (
              <div key={c.id} className="flex items-center justify-between bg-gray-50 rounded p-3">
                <div>
                  <span className="font-medium">{c.capteur_nom}</span>
                  <span className="ml-2 text-sm text-gray-500 font-mono">{c.code_capteur}</span>
                </div>
                <div className="text-sm text-gray-600">
                  {c.bus_type} {c.port_connexion && `• ${c.port_connexion}`}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dernières mesures */}
      {module.dernieres_mesures && module.dernieres_mesures.length > 0 && (
        <div>
          <h3 className="font-medium text-gray-700 mb-3">📈 Dernières Mesures</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {module.dernieres_mesures.map((m, i) => (
              <div key={i} className="bg-blue-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-blue-700">{m.valeur}</div>
                <div className="text-sm text-blue-600">{m.capteur_nom}</div>
                <div className="text-xs text-gray-500">{formatDate(m.timestamp)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Logs récents */}
      {module.logs && module.logs.length > 0 && (
        <div>
          <h3 className="font-medium text-gray-700 mb-3">📜 Logs Récents</h3>
          <div className="bg-gray-900 text-green-400 rounded-lg p-3 font-mono text-xs max-h-48 overflow-y-auto">
            {module.logs.map((log, i) => (
              <div key={i} className="py-1">
                <span className="text-gray-500">[{formatDate(log.timestamp)}]</span>
                <span className={`ml-2 ${getSeverityColor(log.severite)}`}>
                  [{log.severite}]
                </span>
                <span className="ml-2">{log.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ConfigPanel({ module, formData, setFormData, templates, onSync, onApplyTemplate, onCancel }) {
  const handleSeuilChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      config_seuils: {
        ...prev.config_seuils,
        [key]: parseFloat(value)
      }
    }));
  };

  const seuils = formData.config_seuils || {};

  return (
    <div className="space-y-6">
      {/* Templates */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h3 className="font-medium text-yellow-800 mb-3">📋 Appliquer un Template</h3>
        <div className="flex flex-wrap gap-2">
          {templates.map((t) => (
            <button
              key={t.id}
              onClick={() => onApplyTemplate(t.id)}
              className="px-3 py-1 bg-white border border-yellow-300 rounded-full text-sm hover:bg-yellow-100"
            >
              {t.nom}
            </button>
          ))}
        </div>
      </div>

      {/* Seuils configurables */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Température */}
        <div className="bg-red-50 rounded-lg p-4">
          <h4 className="font-medium text-red-800 mb-3">🌡️ Température</h4>
          <div className="space-y-3">
            <SliderInput
              label="Ventilation si >"
              value={seuils.seuil_temp_ventil || 28}
              onChange={(v) => handleSeuilChange('seuil_temp_ventil', v)}
              min={15} max={40} unit="°C"
            />
            <SliderInput
              label="Chauffage si <"
              value={seuils.seuil_temp_chauffe || 12}
              onChange={(v) => handleSeuilChange('seuil_temp_chauffe', v)}
              min={0} max={20} unit="°C"
            />
            <SliderInput
              label="Alerte gel si <"
              value={seuils.seuil_temp_gel || 2}
              onChange={(v) => handleSeuilChange('seuil_temp_gel', v)}
              min={-5} max={10} unit="°C"
            />
          </div>
        </div>

        {/* Humidité */}
        <div className="bg-blue-50 rounded-lg p-4">
          <h4 className="font-medium text-blue-800 mb-3">💧 Humidité Sol</h4>
          <div className="space-y-3">
            <SliderInput
              label="Arrosage si <"
              value={seuils.seuil_hygro_min || 30}
              onChange={(v) => handleSeuilChange('seuil_hygro_min', v)}
              min={10} max={50} unit="%"
            />
            <SliderInput
              label="Stop arrosage si >"
              value={seuils.seuil_hygro_max || 70}
              onChange={(v) => handleSeuilChange('seuil_hygro_max', v)}
              min={50} max={90} unit="%"
            />
          </div>
        </div>

        {/* pH */}
        <div className="bg-purple-50 rounded-lg p-4">
          <h4 className="font-medium text-purple-800 mb-3">🧪 pH</h4>
          <div className="space-y-3">
            <SliderInput
              label="Min acceptable"
              value={seuils.seuil_ph_min || 6.0}
              onChange={(v) => handleSeuilChange('seuil_ph_min', v)}
              min={4} max={7} step={0.1} unit=""
            />
            <SliderInput
              label="Max acceptable"
              value={seuils.seuil_ph_max || 7.5}
              onChange={(v) => handleSeuilChange('seuil_ph_max', v)}
              min={7} max={9} step={0.1} unit=""
            />
          </div>
        </div>

        {/* Intervalle */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-800 mb-3">⏱️ Intervalles</h4>
          <div className="space-y-3">
            <SliderInput
              label="Mesures toutes les"
              value={formData.interval_mesure || 15}
              onChange={(v) => setFormData(prev => ({ ...prev, interval_mesure: parseInt(v) }))}
              min={1} max={60} step={1} unit="min"
            />
          </div>
        </div>
      </div>

      {/* Config JSON brute */}
      <details className="bg-gray-100 rounded-lg">
        <summary className="cursor-pointer p-3 font-medium text-gray-700">
          🔧 Configuration JSON brute
        </summary>
        <pre className="p-3 text-xs overflow-x-auto bg-gray-900 text-green-400 rounded-b-lg">
          {JSON.stringify(formData.config_seuils, null, 2)}
        </pre>
      </details>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          onClick={onCancel}
          className="px-4 py-2 border rounded-lg hover:bg-gray-50"
        >
          Annuler
        </button>
        <button
          onClick={onSync}
          className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
        >
          🔄 Synchroniser vers ESP32
        </button>
      </div>
    </div>
  );
}

function SliderInput({ label, value, onChange, min, max, step = 1, unit }) {
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span>{label}</span>
        <span className="font-mono font-bold">{value}{unit}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
      />
    </div>
  );
}

function InfoCard({ label, value, mono = false }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div className="text-xs text-gray-500 uppercase">{label}</div>
      <div className={`font-medium ${mono ? 'font-mono text-sm' : ''}`}>{value}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

function getEmptyFormData() {
  return {
    code_module: '',
    nom: '',
    description: '',
    localisation: '',
    mac_address: '',
    protocole: 'wifi',
    interval_mesure: 15,
    config_seuils: getDefaultSeuils()
  };
}

function getDefaultSeuils() {
  return {
    seuil_temp_ventil: 28,
    seuil_temp_chauffe: 12,
    seuil_temp_gel: 2,
    seuil_hygro_min: 30,
    seuil_hygro_max: 70,
    seuil_ph_min: 6.0,
    seuil_ph_max: 7.5
  };
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatDuration(minutes) {
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 1440) return `${Math.floor(minutes / 60)}h`;
  return `${Math.floor(minutes / 1440)}j`;
}

function getSeverityColor(severity) {
  const colors = {
    debug: 'text-gray-400',
    info: 'text-blue-400',
    warning: 'text-yellow-400',
    error: 'text-orange-400',
    critical: 'text-red-400'
  };
  return colors[severity] || 'text-gray-400';
}
